/**
 * Obsolete icon registry — icons migrated from legacy @uikit only to unblock
 * its removal, pending design review. The curated, design-approved set lives
 * in ICON_REGISTRY (Icon.tsx); this bucket must never duplicate it.
 */

import {
	IconAlertCircleFilled,
	IconAlertTriangleFilled,
	IconArrowsMove,
	IconBuilding,
	IconBulbFilled,
	IconCalendarCheck,
	IconClipboardText,
	IconClock,
	IconDotsVertical,
	IconEdit,
	IconEyeOff,
	IconFilter,
	IconFolder,
	IconFolderMinus,
	IconFolderShare,
	IconFolderStar,
	IconGif,
	IconHeart,
	IconHeartFilled,
	IconHelp,
	IconHighlight,
	IconHome,
	IconHomeFilled,
	IconInfoCircleFilled,
	IconLanguage,
	IconLibraryPhoto,
	IconMapPinPlus,
	IconMenu2,
	IconMessageDots,
	IconMoodSmile,
	IconPlaylist,
	IconPlug,
	IconRepeat,
	IconReplace,
	IconSettings,
	IconShare,
	IconSortDescending,
	IconSquareLetterA,
	IconTextColor,
	IconThumbDown,
	IconThumbUp,
	IconUser,
	IconUserFilled,
	IconUserPlus,
	IconUserShare,
	IconUsers,
	IconUsersGroup,
	IconVideoFilled,
	IconZoomIn,
	IconZoomOut,
} from "@tabler/icons-react";

import {
	AIStarsLegacyIcon,
	AspectRatioFourFiveLegacyIcon,
	AspectRatioHorizontalLegacyIcon,
	AspectRatioSquareLegacyIcon,
	AspectRatioVerticalLegacyIcon,
	AudioDescriptionOffLegacyIcon,
	BuildingsEmptyStateLegacyIcon,
	ClassesOutlineLegacyIcon,
	ClassesSolidLegacyIcon,
	CombineLegacyIcon,
	DisappearingInkLegacyIcon,
	DottedLeftChevronLegacyIcon,
	DottedRightChevronLegacyIcon,
	EffectLegacyIcon,
	IntegrationsSolidLegacyIcon,
	InteractiveVideoOutlineLegacyIcon,
	InteractiveVideoSolidLegacyIcon,
	LearningPathLegacyIcon,
	LevelsLegacyIcon,
	PremiumBadgeLegacyIcon,
	ProjectBinSolidLegacyIcon,
	ProjectLegacyIcon,
	ProjectSolidLegacyIcon,
	RecordLegacyIcon,
	ReplaceImageLegacyIcon,
	StoryboardLegacyIcon,
	TextFormatLegacyIcon,
	TextShadowLegacyIcon,
	TextStrokeSlimLegacyIcon,
	TextStrokeThickLegacyIcon,
	TrackLegacyIcon,
	TransitionHintLegacyIcon,
	TransitionLegacyIcon,
	VideoSourceOutlineLegacyIcon,
	VideoSourceSolidLegacyIcon,
	WaveformLegacyIcon,
	type ObsoleteIconComponent,
} from "./IconObsoleteLegacy";

export type { ObsoleteIconComponent, ObsoleteIconProps } from "./IconObsoleteLegacy";

export const OBSOLETE_ICON_REGISTRY = {
	// Tabler swaps (visually confirmed in docs/uikit3-icons-migration.md)
	"alert-circle-filled": IconAlertCircleFilled,
	"alert-triangle-filled": IconAlertTriangleFilled,
	"arrows-move": IconArrowsMove,
	building: IconBuilding,
	"bulb-filled": IconBulbFilled,
	"clipboard-text": IconClipboardText,
	clock: IconClock,
	edit: IconEdit,
	"eye-off": IconEyeOff,
	filter: IconFilter,
	folder: IconFolder,
	"folder-share": IconFolderShare,
	gif: IconGif,
	heart: IconHeart,
	"heart-filled": IconHeartFilled,
	help: IconHelp,
	home: IconHome,
	"home-filled": IconHomeFilled,
	"info-circle-filled": IconInfoCircleFilled,
	language: IconLanguage,
	"map-pin-plus": IconMapPinPlus,
	"menu-2": IconMenu2,
	"message-dots": IconMessageDots,
	playlist: IconPlaylist,
	plug: IconPlug,
	repeat: IconRepeat,
	replace: IconReplace,
	settings: IconSettings,
	share: IconShare,
	"sort-descending": IconSortDescending,
	"thumb-down": IconThumbDown,
	"thumb-up": IconThumbUp,
	user: IconUser,
	"user-filled": IconUserFilled,
	"user-plus": IconUserPlus,
	"user-share": IconUserShare,
	users: IconUsers,
	"users-group": IconUsersGroup,
	"video-filled": IconVideoFilled,
	// Tabler swaps — 2026-08-04 additions (edu/editor + post-table), visually verified
	"calendar-check": IconCalendarCheck,
	"dots-vertical": IconDotsVertical,
	"folder-minus": IconFolderMinus,
	"folder-star": IconFolderStar,
	highlight: IconHighlight,
	"library-photo": IconLibraryPhoto,
	"mood-smile": IconMoodSmile,
	"square-letter-a": IconSquareLetterA,
	"text-color": IconTextColor,
	"zoom-in": IconZoomIn,
	"zoom-out": IconZoomOut,

	// Legacy WeVideo glyphs (copied SVGs, visual no-op — see IconObsoleteLegacy.tsx)
	"ai-stars": AIStarsLegacyIcon,
	"audio-description-off": AudioDescriptionOffLegacyIcon,
	"buildings-empty-state": BuildingsEmptyStateLegacyIcon,
	"classes-outline": ClassesOutlineLegacyIcon,
	"classes-solid": ClassesSolidLegacyIcon,
	combine: CombineLegacyIcon,
	"disappearing-ink": DisappearingInkLegacyIcon,
	"dotted-left-chevron": DottedLeftChevronLegacyIcon,
	"dotted-right-chevron": DottedRightChevronLegacyIcon,
	effect: EffectLegacyIcon,
	"integrations-solid": IntegrationsSolidLegacyIcon,
	"interactive-video-outline": InteractiveVideoOutlineLegacyIcon,
	"interactive-video-solid": InteractiveVideoSolidLegacyIcon,
	"learning-path": LearningPathLegacyIcon,
	"premium-badge": PremiumBadgeLegacyIcon,
	project: ProjectLegacyIcon,
	"project-solid": ProjectSolidLegacyIcon,
	record: RecordLegacyIcon,
	"replace-image": ReplaceImageLegacyIcon,
	storyboard: StoryboardLegacyIcon,
	transition: TransitionLegacyIcon,
	"video-source-outline": VideoSourceOutlineLegacyIcon,
	"video-source-solid": VideoSourceSolidLegacyIcon,
	waveform: WaveformLegacyIcon,
	// Legacy WeVideo glyphs — 2026-08-04 additions
	"aspect-ratio-four-five": AspectRatioFourFiveLegacyIcon,
	"aspect-ratio-horizontal": AspectRatioHorizontalLegacyIcon,
	"aspect-ratio-square": AspectRatioSquareLegacyIcon,
	"aspect-ratio-vertical": AspectRatioVerticalLegacyIcon,
	levels: LevelsLegacyIcon,
	"project-bin-solid": ProjectBinSolidLegacyIcon,
	"text-format": TextFormatLegacyIcon,
	"text-shadow": TextShadowLegacyIcon,
	"text-stroke-slim": TextStrokeSlimLegacyIcon,
	"text-stroke-thick": TextStrokeThickLegacyIcon,
	track: TrackLegacyIcon,
	"transition-hint": TransitionHintLegacyIcon,
} as const satisfies Record<string, ObsoleteIconComponent>;

export type ObsoleteIconName = keyof typeof OBSOLETE_ICON_REGISTRY;

export const OBSOLETE_ICON_NAMES = Object.keys(OBSOLETE_ICON_REGISTRY) as ObsoleteIconName[];
